import { RequestParsingService } from '@/views/ibis/requestParsing.service';

export interface UiPreviewEntry {
  /** Route name, as registered in the router. */
  routeName: string;
  /** Human readable label shown in the gallery. */
  label: string;
  /** Query params that make the screen render something meaningful. */
  query?: Record<string, string>;
  /** Notes shown under the entry (e.g. why it may look empty). */
  note?: string;
}

export interface UiPreviewArea {
  key: string;
  label: string;
  description: string;
  entries: UiPreviewEntry[];
}

/** A representative Ibis request payload, base64url-encoded like the real flow. */
function sampleRequest(overrides: Record<string, unknown> = {}): string {
  return RequestParsingService.toUrlEncodedJson({
    returnUrl: 'https://example.telenor.no/return',
    redirectUri: 'https://example.telenor.no/callback',
    loginHint: '99966634',
    language: 'no',
    loginContext: 'login',
    authorizeParams: {},
    ...overrides,
  });
}

export const uiPreviewAreas: UiPreviewArea[] = [
  {
    key: 'ibis',
    label: 'Ibis',
    description: 'Primary identification, login, sign-up and step-up screens.',
    entries: [
      {
        routeName: 'identify',
        label: 'Identify (phone number)',
        query: { requestUrlEncoded: sampleRequest() },
      },
      {
        routeName: 'identify',
        label: 'Identify - bad input error',
        query: { requestUrlEncoded: sampleRequest({ errorCode: 40002 }) },
      },
      {
        routeName: 'ibis-login',
        label: 'Login - IdP picker',
        query: {
          requestUrlEncoded: sampleRequest({ idps: ['no.bankid.vipps', 'no.telenor.employee'] }),
        },
      },
      {
        routeName: 'ibis-login',
        label: 'Login - KYC context',
        query: {
          requestUrlEncoded: sampleRequest({
            loginContext: 'kyc',
            idps: ['no.bankid.vipps'],
          }),
        },
      },
      {
        routeName: 'ibis-sign-up-phone',
        label: 'Sign up (by phone)',
        query: { request: 'preview-request-id' },
        note: 'Backend call will fail locally; layout still renders.',
      },
      {
        routeName: 'ibis-sign-up-person',
        label: 'Sign up (by person)',
        query: { request: 'preview-request-id' },
        note: 'Backend call will fail locally; layout still renders.',
      },
      {
        routeName: 'ibis-sign-up-verify',
        label: 'Verify user (OTP)',
        query: { codeId: 'preview-code-id' },
        note: 'Backend call will fail locally.',
      },
      { routeName: 'ibis-sign-up-validate', label: 'Validate (session expired)' },
      {
        routeName: 'ibis-step-up',
        label: 'Step up (BankID)',
        query: { request: 'preview-request-id' },
        note: 'Backend call will fail locally.',
      },
      { routeName: 'ibis-national-id-info', label: 'National ID info' },
      { routeName: 'ibis-cancel-warning', label: 'Cancel warning' },
      { routeName: 'ibis-logout', label: 'Logout confirmation' },
      { routeName: 'correlation-failed', label: 'Correlation failed (cookies)' },
    ],
  },
  {
    key: 'elvis',
    label: 'Elvis',
    description: 'Username/password login, registration and password recovery.',
    entries: [
      { routeName: 'elv-login', label: 'Login', query: { request: 'preview-request' } },
      {
        routeName: 'elv-register',
        label: 'Register new account',
        query: { request: 'preview-request' },
      },
      {
        routeName: 'elv-forgot-username',
        label: 'Forgot username',
        query: { request: 'preview-request' },
      },
      {
        routeName: 'elv-forgot-password',
        label: 'Forgot password',
        query: { request: 'preview-request' },
      },
      {
        routeName: 'elv-set-password',
        label: 'Set new password',
        query: { username: 'oda@nordmann.no', code: 'preview-code' },
        note: 'Shows the "wrong or expired link" state unless the backend validates the code.',
      },
    ],
  },
  {
    key: 'kookaburra',
    label: 'Kookaburra',
    description: 'MSISDN identification and SMS/IVR one-time-code screens.',
    entries: [
      { routeName: 'koo-identify', label: 'Identify (phone number)' },
      { routeName: 'koo-login', label: 'OTP - sent by SMS', query: { phoneNumber: '99966634' } },
      {
        routeName: 'koo-login',
        label: 'OTP - delivered by call',
        query: { phoneNumber: '99966634', ivr: 'true' },
      },
      {
        routeName: 'koo-login',
        label: 'OTP - wrong code error',
        query: { phoneNumber: '99966634', error: '40401' },
      },
      { routeName: 'koo-error', label: 'OTP error', query: { ivr: 'true' } },
      {
        routeName: 'koo-error',
        label: 'OTP error - with IVR fallback',
        query: { attemptFallback: 'true' },
      },
    ],
  },
  {
    key: 'cassowary',
    label: 'Cassowary',
    description: 'Mutual-TLS failure screen.',
    entries: [{ routeName: 'mtls-failed', label: 'mTLS failed' }],
  },
  {
    key: 'device',
    label: 'Device flow',
    description: 'Device activation, consent and result screens.',
    entries: [
      {
        routeName: 'Activate',
        label: 'Activate (enter user code)',
        query: { userCode: '123456789' },
      },
      {
        routeName: 'DeviceLogin',
        label: 'Login (redirecting)',
        query: { userCode: '123456789' },
        note: 'Immediately attempts an OIDC redirect.',
      },
      {
        routeName: 'oath-callback',
        label: 'Consent',
        note: 'Requires a real OIDC callback; shows the error state otherwise.',
      },
      { routeName: 'Success', label: 'Success' },
      { routeName: 'Failure', label: 'Failure' },
    ],
  },
];
